import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLines, FadeUp, ChapterLabel } from "@/components/Reveal";
import ValuesMarquee from "@/components/ValuesMarquee";
import { useSiteData, defaultSettings } from "@/api";

const PORTRAIT_IMG = "/chantiers/a-propos.jpg";

const valeurs = [
  {
    n: "01",
    titre: "La rigueur avant tout",
    texte:
      "Un support mal préparé gâche la plus belle des peintures. Alexis ne passe jamais cette étape : protection, rebouchage, ponçage, sous-couche.",
  },
  {
    n: "02",
    titre: "Le soin du détail",
    texte:
      "Une arête nette, un raccord invisible, une teinte exactement comme vous l'imaginiez. C'est dans ces détails qu'on reconnaît un travail d'artisan.",
  },
  {
    n: "03",
    titre: "Le respect des délais",
    texte:
      "Un chantier qui s'éternise épuise tout le monde. Alexis annonce un planning réaliste — et s'y tient.",
  },
];

const APropos = () => {
  const site = useSiteData({ chantiers: null, services: null, settings: defaultSettings });
  const villes = site.settings?.villes?.length ? site.settings.villes : defaultSettings.villes;

  return (
    <>
      <Seo
        title="À propos — Alexis Delcey, artisan peintre à Angoulême & Cognac | Alexis Peinture"
        description="Découvrez Alexis Delcey, artisan peintre en bâtiment dans le Grand Angoulême et le Grand Cognac. Rigueur, soin du détail et respect des délais."
      />

      <section className="px-6 pb-20 pt-40 lg:px-12 lg:pt-48">
        <div className="mx-auto max-w-7xl">
          <ChapterLabel number="01" label="À propos" />
          <h1
            className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
            data-testid="apropos-title"
          >
            <MaskedLines
              lines={[
                "Un artisan,",
                <span key="l2">
                  une <em className="italic text-clay">exigence.</em>
                </span>,
              ]}
            />
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-12" data-testid="apropos-portrait">
        <div className="grid items-start gap-16 lg:grid-cols-5">
          <FadeUp className="lg:col-span-2">
            <div className="overflow-hidden bg-white p-6">
              <img
                src={PORTRAIT_IMG}
                alt="Logo Alexis Peintures — la vie en couleurs"
                loading="lazy"
                className="aspect-square w-full object-contain transition-transform duration-[2000ms] ease-out hover:scale-[1.04]"
              />
            </div>
          </FadeUp>
          <div className="lg:col-span-3">
            <FadeUp delay={0.1}>
              <h2 className="font-serif text-3xl tracking-tight sm:text-4xl">
                Alexis Delcey, peintre en bâtiment
              </h2>
              <div className="mt-8 max-w-xl space-y-6 leading-relaxed text-bark">
                <p>
                  Alexis Peinture est née en septembre 2025, d'une conviction
                  simple : un intérieur bien peint change la façon dont on vit
                  chez soi. Installé entre Angoulême et Cognac, Alexis a choisi
                  de se consacrer à ce qu'il fait de mieux — la peinture
                  intérieure décorative.
                </p>
                <p>
                  Jusqu'ici, ses clients le recommandent de bouche à oreille.
                  C'est sa plus belle carte de visite, et ce site est là pour
                  vous permettre de vérifier par vous-même : le travail parle,
                  les finitions aussi.
                </p>
                <p>
                  Pas de sous-traitance, pas d'intermédiaire : c'est Alexis qui
                  visite votre chantier, établit votre devis et tient le pinceau.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <ValuesMarquee />

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-36" data-testid="apropos-valeurs">
        <ChapterLabel number="02" label="Valeurs" />
        <FadeUp delay={0.1}>
          <h2 className="mt-8 max-w-2xl font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
            Trois principes, <em className="italic text-clay">zéro compromis.</em>
          </h2>
        </FadeUp>
        <div className="mt-20 grid gap-12 md:grid-cols-3">
          {valeurs.map((v, i) => (
            <FadeUp key={v.n} delay={i * 0.12}>
              <p className="font-serif text-5xl italic text-clay">{v.n}</p>
              <h3 className="mt-5 font-serif text-2xl tracking-tight">{v.titre}</h3>
              <p className="mt-4 text-sm leading-relaxed text-bark">{v.texte}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="bg-plaster/60 py-24 lg:py-32" data-testid="apropos-zone">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-16 lg:grid-cols-2">
            <div>
              <ChapterLabel number="03" label="Zone d'intervention" />
              <FadeUp delay={0.1}>
                <h2 className="mt-8 font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
                  Grand Angoulême <em className="italic text-clay">&amp;</em> Grand Cognac
                </h2>
                <p className="mt-8 max-w-md leading-relaxed text-bark">
                  Alexis intervient dans un rayon couvrant les deux agglomérations
                  et leurs communes. Vous êtes un peu plus loin ? Contactez-le —
                  il étudie chaque demande.
                </p>
              </FadeUp>
              <FadeUp delay={0.2}>
                <Link
                  to="/contact"
                  data-testid="apropos-cta-devis"
                  className="group mt-10 inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm font-medium text-cream transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-ink"
                >
                  Vérifier ma zone
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </FadeUp>
            </div>
            <FadeUp delay={0.15}>
              <div className="flex flex-wrap gap-3" data-testid="zone-villes">
                {villes.map((v) => (
                  <span
                    key={v}
                    className="flex items-center gap-2 rounded-full border border-ink/15 bg-cream px-5 py-2.5 text-sm text-bark"
                  >
                    <MapPin size={13} className="text-clay" /> {v}
                  </span>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
};

export default APropos;
